import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { terminologyApi, type TermProfile } from '@/api/terminologyApi'
import { applyLocales } from '@/i18n/i18n'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  profiles: TermProfile[]
}

const slug = (s: string) => s.trim().toLowerCase()

export function AddLanguageModal({ open, onOpenChange, profiles }: Props) {
  const queryClient = useQueryClient()
  const base = profiles.find((p) => p.isBase)

  const [lang, setLang] = useState('')
  const [variant, setVariant] = useState('')
  const [label, setLabel] = useState('')
  const [fallback, setFallback] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setLang('')
    setVariant('')
    setLabel('')
    setFallback('')
    setError(null)
  }

  const code = `${slug(lang) || 'lang'}-${slug(variant) || 'variant'}`
  const canSubmit = slug(lang) && slug(variant) && label.trim()

  async function handleCreate() {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      await terminologyApi.createProfile({
        lang: slug(lang),
        variant: slug(variant),
        label: label.trim(),
        fallback: fallback || base?.id || undefined,
      })
      const fresh = await queryClient.fetchQuery({
        queryKey: ['terms', 'profiles'],
        queryFn: terminologyApi.listProfiles,
        // The list just changed: a copy cached within staleTime would be applied,
        // and persisted for the next load, without the new language.
        staleTime: 0,
      })
      applyLocales(fresh)
      reset()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create language.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add language</DialogTitle>
          <DialogDescription>
            Creates a terminology profile that inherits all terms from its fallback until you
            translate them.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="loc-lang">Language</Label>
              <Input
                id="loc-lang"
                placeholder="es"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="loc-variant">Variant</Label>
              <Input
                id="loc-variant"
                placeholder="standard"
                value={variant}
                onChange={(e) => setVariant(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="loc-label">Label</Label>
            <Input
              id="loc-label"
              placeholder="Estándar (Español)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Inherits from</Label>
            <Select value={fallback || base?.id} onValueChange={setFallback}>
              <SelectTrigger>
                <SelectValue placeholder="Select a fallback" />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            Code will be <code className="font-mono">{code}</code>.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canSubmit || saving}>
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Create language
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
